/**
 * Role Definitions
 *
 * Single source of truth for party role → account mapping → billing config.
 * Adding a new role requires:
 *   1. Add role constant to party-types.ts (ROLE_xxx, PARTY_ROLES)
 *   2. Add entry here with account mappings and billing config
 *   3. Add corresponding backend entry in domain/role_definitions.go
 *   4. Ensure head accounts exist (via chart-of-accounts or activation)
 */

import {
  ROLE_CUSTOMER,
  ROLE_SUPPLIER,
} from '../types/billing/party-types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AccountMapping {
  /** What this account is for: "receivable", "payable", "advance", "vendor_advance", "deposit" */
  purpose: string
  /** Explicit head-account code (e.g. "1200"). Subledger code = "{parentAccountCode}-{partyCode}". */
  parentAccountCode: string
  /** System tag of the parent/head account (fallback when parentAccountCode is blank) */
  parentTag: string
  /** Account type of the parent */
  parentAccountType: string
  /** Account subtype to find parent if tag lookup fails */
  parentSubtype: string
  /** Human-readable description for the mapping */
  description: string
  /** Suffix appended to party name in the subledger account name */
  ledgerSuffix: string
}

export interface BillingConfig {
  /** Can we issue invoices TO this party? (they owe us) */
  canReceiveInvoices: boolean
  /** Can this party issue invoices TO us? (we owe them) */
  canIssueInvoices: boolean
  /** Valid invoice types for this role */
  invoiceTypes: string[]
  /** Default payment direction: 'inward' or 'outward' */
  paymentDirection: 'inward' | 'outward'
}

export interface RoleDefinition {
  /** Unique role identifier */
  role: string
  /** Display label */
  label: string
  /** Account mappings for subledger creation */
  accountMappings: AccountMapping[]
  /** Billing configuration */
  billing: BillingConfig
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const ROLE_DEFINITIONS: Record<string, RoleDefinition> = {
  [ROLE_CUSTOMER]: {
    role: ROLE_CUSTOMER,
    label: 'Customer',
    accountMappings: [
      {
        purpose: 'receivable',
        parentAccountCode: '1200',
        parentTag: 'trade_receivable',
        parentAccountType: 'ACCOUNT_TYPE_ASSET',
        parentSubtype: 'Receivable',
        description: 'Accounts Receivable',
        ledgerSuffix: 'Receivable',
      },
      {
        purpose: 'advance',
        parentAccountCode: '2700',
        parentTag: 'customer_advance',
        parentAccountType: 'ACCOUNT_TYPE_LIABILITY',
        parentSubtype: 'Customer Advances',
        description: 'Customer Advance',
        ledgerSuffix: 'Advance',
      },
    ],
    billing: {
      canReceiveInvoices: true,
      canIssueInvoices: false,
      invoiceTypes: ['b2b', 'b2c', 'export', 'credit_note', 'debit_note'],
      paymentDirection: 'inward',
    },
  },

  [ROLE_SUPPLIER]: {
    role: ROLE_SUPPLIER,
    label: 'Supplier',
    accountMappings: [
      {
        purpose: 'payable',
        parentAccountCode: '2100',
        parentTag: 'trade_payable',
        parentAccountType: 'ACCOUNT_TYPE_LIABILITY',
        parentSubtype: 'Payable',
        description: 'Accounts Payable',
        ledgerSuffix: 'Payable',
      },
      {
        purpose: 'vendor_advance',
        parentAccountCode: '1700',
        parentTag: 'vendor_advance',
        parentAccountType: 'ACCOUNT_TYPE_ASSET',
        parentSubtype: 'Vendor Advances',
        description: 'Vendor Advance',
        ledgerSuffix: 'Advance',
      },
    ],
    billing: {
      canReceiveInvoices: false,
      canIssueInvoices: true,
      invoiceTypes: ['vendor_bill', 'vendor_credit_note'],
      paymentDirection: 'outward',
    },
  },

}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get a role definition by role identifier. */
export function getRoleDefinition(role: string): RoleDefinition | undefined {
  return ROLE_DEFINITIONS[role]
}

/** Get account mapping for a role and purpose (e.g., "customer" + "receivable"). */
export function getAccountMapping(role: string, purpose: string): AccountMapping | undefined {
  const def = ROLE_DEFINITIONS[role]
  return def?.accountMappings.find(m => m.purpose === purpose)
}

/** Search all roles a party has for a matching account purpose. */
export function findAccountMappingByPurpose(roles: string[], purpose: string): AccountMapping | undefined {
  for (const role of roles) {
    const mapping = getAccountMapping(role, purpose)
    if (mapping) { return mapping }
  }
  return undefined
}

/** Get all role definitions as an array. */
export function allRoleDefinitions(): RoleDefinition[] {
  return Object.values(ROLE_DEFINITIONS)
}
